import { Role } from '@/models/AccountEnums';
import { buildRoleAwarePrefix } from '@/utils/api';
import {
  advisorRequiresInvestorId,
  advisorScopeMissingError,
} from '@/utils/advisorScoping';
import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';

export interface PerformanceReportItem {
  UPRAccountBenchmarkId: number;
  ParentId?: number | null;
  PortfolioId?: number | null;
  ClassId?: number | null;
  Description: string;
  BenchmarkName?: string | null;
  EndingMarketValue?: number | null;
  PerformancePeriod1?: number | null;
  PerformancePeriod2?: number | null;
  PerformancePeriod3?: number | null;
  PerformancePeriod4?: number | null;
  PerformancePeriod5?: number | null;
  BenchmarkPeriod1?: number | null;
  BenchmarkPeriod2?: number | null;
  BenchmarkPeriod3?: number | null;
  BenchmarkPeriod4?: number | null;
  BenchmarkPeriod5?: number | null;
  LastUpdated?: string | null;
}

export type NotHeldSymbol = 'Blank' | 'Dash' | 'N/A';

export interface PerformanceReportConfig {
  NotHeldSymbol: NotHeldSymbol;
  DisclaimerString?: string;
  PerformancePeriod1Enabled?: boolean;
  PerformancePeriod2Enabled?: boolean;
  PerformancePeriod3Enabled?: boolean;
  PerformancePeriod4Enabled?: boolean;
  PerformancePeriod5Enabled?: boolean;
  PerformancePeriod1DashboardEnabled?: boolean;
  PerformancePeriod2DashboardEnabled?: boolean;
  PerformancePeriod3DashboardEnabled?: boolean;
  PerformancePeriod4DashboardEnabled?: boolean;
  PerformancePeriod5DashboardEnabled?: boolean;
}

export interface PerformanceReportsResponse {
  Item1: PerformanceReportItem[];
  Item2: PerformanceReportConfig;
}

export interface GetPerformanceReportsParams {
  investorId?: number;
}

export const performanceReportsApi = createApi({
  reducerPath: 'performanceReportsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['PerformanceReports'],
  endpoints: (builder) => ({
    getPerformanceReports: builder.query<
      PerformanceReportsResponse,
      GetPerformanceReportsParams | void
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole ?? Role.Investor;
        const investorId = params?.investorId;
        if (advisorRequiresInvestorId(currentRole, investorId)) {
          return { error: advisorScopeMissingError() };
        }
        const result = await baseQuery({
          url: `${buildRoleAwarePrefix(currentRole, investorId)}/PerformanceReports`,
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error } as any;
        }
        return { data: result.data as PerformanceReportsResponse };
      },
      providesTags: (_result) => [{ type: 'PerformanceReports', id: 'LIST' }],
    }),
  }),
});

export const { useGetPerformanceReportsQuery } = performanceReportsApi;
